import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Download, Trash2, Play, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function History() {
  const navigate = useNavigate();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteCompId, setDeleteCompId] = useState(null);

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`);
      // Sort by date descending
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setCompetitions(sorted);
    } catch (error) {
      console.error("Error fetching competitions:", error);
      toast.error("Failed to load competitions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const deleteCompetition = async () => {
    if (!deleteCompId) return;
    try {
      await axios.delete(`${API}/competitions/${deleteCompId}`);
      setDeleteCompId(null);
      fetchCompetitions();
      toast.success("Competition deleted");
    } catch (error) {
      console.error("Error deleting competition:", error);
      toast.error("Failed to delete competition");
    }
  };

  const exportResults = (compId) => {
    window.open(`${API}/competitions/${compId}/export/csv`, "_blank");
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      setup: { label: "Setup", className: "bg-gray-200 text-gray-700" },
      round1: { label: "Round 1", className: "bg-blue-100 text-blue-700" },
      round2: { label: "Round 2", className: "bg-blue-200 text-blue-800" },
      round3: { label: "Finals", className: "bg-purple-200 text-purple-800" },
      completed: { label: "Completed", className: "bg-green-200 text-green-800" }
    };
    return statusMap[status] || { label: status, className: "bg-gray-200" };
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", { 
        year: "numeric", 
        month: "short", 
        day: "numeric" 
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <p className="font-heading text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">
            Competition History
          </h1>
          <p className="text-muted-foreground mt-1">
            {competitions.length} competition{competitions.length !== 1 ? "s" : ""} recorded
          </p>
        </div>
        <Button onClick={() => navigate("/setup")} data-testid="new-competition-btn">
          New Competition
        </Button>
      </div>

      {/* Competitions list */}
      {competitions.length > 0 ? (
        <Card data-testid="competitions-table-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Competition</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Poets</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {competitions.map((comp, index) => {
                  const statusInfo = getStatusDisplay(comp.status);
                  return (
                    <TableRow key={comp.id} data-testid={`competition-row-${index}`}>
                      <TableCell className="font-heading font-medium">
                        {comp.name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span className="font-mono text-sm">
                            {formatDate(comp.date || comp.created_at)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">
                        {comp.poets?.length || 0}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${statusInfo.className}`}>
                          {statusInfo.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`actions-btn-${index}`}>
                              Actions
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {comp.status === "setup" ? (
                              <DropdownMenuItem 
                                onClick={() => navigate(`/setup/${comp.id}`)}
                                data-testid={`edit-btn-${index}`}
                              >
                                <Play className="w-4 h-4 mr-2" />
                                Continue Setup
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => navigate(`/competition/${comp.id}`)}
                                data-testid={`view-btn-${index}`}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Competition
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => exportResults(comp.id)}
                              data-testid={`export-btn-${index}`}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Export CSV
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setDeleteCompId(comp.id)}
                              className="text-red-600"
                              data-testid={`delete-btn-${index}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="empty-history-card">
          <CardContent className="py-12 text-center">
            <h3 className="font-heading font-semibold text-xl mb-2">No Competitions Yet</h3>
            <p className="text-muted-foreground mb-6">
              Create your first competition to get started.
            </p>
            <Button onClick={() => navigate("/setup")} data-testid="create-first-btn">
              Create Competition
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteCompId} onOpenChange={() => setDeleteCompId(null)}>
        <AlertDialogContent data-testid="delete-competition-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Competition?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the competition and all associated data. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-comp-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteCompetition} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-comp-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
